import { get } from "@rails/request.js";

type ComboboxConfig = {
  comboboxUrl: string;
  comboboxQueryParam: string;
  comboboxMinLength: string;
  comboboxInputName: string;
  comboboxDeferFetch: string;
};

const optionSelector = "[role='option']:not([aria-disabled])";

export default (inputValue = "") => ({
  activeOption: null,
  selectedOption: null,
  selectedValue: "",
  config: {},
  results: {
    content: "",
    visible: false,
    isLoading: false,
    defaultHTML: "",
  },
  showClear: false,

  init() {
    this.config = this.$el.dataset as ComboboxConfig;
    this.selectedValue = inputValue;

    // Show clear button if there's a pre-populated value
    if (inputValue && inputValue.length > 0) {
      this.showClear = true;
    }

    this.$anchor(
      this.$refs.inputContainer,
      this.$refs.resultsContainer,
      null,
      null,
      true
    );

    if (!this.deferFetch) {
      this.fetchDefaultResults();
    }

    this.$watch("results.visible", () => {
      if (this.hasSearchQuery()) {
        this.showClear = true;
      }

      this.$dispatch("combobox:toggle", {
        action: this.results.visible ? "open" : "close",
        inputName: this.config.comboboxInputName,
      });
    });
  },

  /**
   * Whether options are fetched on first interaction instead of on page load.
   */
  get deferFetch(): boolean {
    return this.config.comboboxDeferFetch === "true";
  },

  /**
   * Reveals the list of menu items without any bearing on the search query.
   * This is currently only used when the user clicks on the dropdown button
   * beside the combobox input field, which reveals all of the available
   * options, if any are available.
   */
  async toggleResults() {
    if (this.results.visible) {
      this.resetResults();
      return;
    }

    if (this.deferFetch) {
      await this.fetchResults("");
    } else {
      this.results.visible = true;

      if (this.options.length > 0) {
        this.activeOption = this.options[0];
      }
    }

    // Focus the input field when opening the dropdown
    this.$refs.searchInput.focus();
  },

  /**
   * Determines if the provided option in the listbox is active in the sense
   * that the user is hovered over the option, and one click or keystroke away
   * from selecting the option.
   */
  isActive(itemEl: Element) {
    return this.activeOption?.id === itemEl.id;
  },

  /**
   * Determines if the provided option in the listbox is selected.
   */
  isSelected(itemEl: Element) {
    return (
      this.selectedValue === itemEl.getAttribute("data-listbox-option-value")
    );
  },

  hasSearchQuery() {
    const query = this.$refs.searchInput.value.trim();
    return query && query.length > 0;
  },

  onInputChange() {
    this.selectedValue = "";

    const query = this.$refs.searchInput.value.trim();

    if (query && query.length >= this.config.comboboxMinLength) {
      this.fetchResults(query);
    } else {
      this.resetResults();
      if (query.length === 0) {
        this.showClear = false;
        this.$dispatch("combobox:clear", {
          inputName: this.config.comboboxInputName,
        });
      }
    }
  },

  onResultsClick(event: any) {
    if (!(event.target instanceof Element)) {
      return;
    }
    const selected = event.target.closest(optionSelector);
    if (selected) {
      this.commit(event.target);
    }
  },

  /**
   * Selects the active item on enter key press.
   */
  onEnterKeydown() {
    if (this.activeOption && this.results.visible) {
      this.commit(this.activeOption);
    }
  },

  async onArrowDownKey() {
    if (this.results.visible) {
      this.selectSibling(true);
      return;
    }

    if (this.deferFetch) {
      await this.fetchResults("");
    } else {
      this.results.visible = true;
    }
  },

  /**
   * Hides the dropdown on clicks outside the component. If there is no pre-
   * selected value, clears the search input value and hides the clear button.
   */
  onClickOutside() {
    this.resetResults();

    if (this.selectedValue === "") {
      this.$refs.searchInput.value = "";
      this.showClear = false;
    }
  },

  /**
   * Clears the selected item, resets the search result list, clears the search
   * query input field, and hides the clear button, effectively setting the
   * combobox back to its initial state.
   */
  clear() {
    this.$refs.searchInput.value = "";
    this.$refs.searchInput.focus();
    this.selectedValue = "";
    this.selectedOption = null;
    this.resetResults();
    this.showClear = false;
    this.$dispatch("combobox:clear", {
      inputName: this.config.comboboxInputName,
    });
  },

  /**
   * Clears the selected item and the search query input field, effectively
   * setting the combobox back to its initial state. Works similar to the clear
   * method above but for externally-triggered resets such as a reset button on
   * wrapping form, which doesn't require focus to be returned to the search
   * input, or the search results list to be hidden.
   */
  reset() {
    this.$refs.searchInput.value = "";
    this.selectedValue = "";
    this.showClear = false;
  },

  /**
   * Returns a sibling element from the list of result options.
   *
   * This is used by keyboard events to support navigation through
   * results via the up/down arrow keys.
   *
   * @param next - If true, returns next sibling, else returns the previous.
   */
  selectSibling(next: boolean) {
    const options = this.options;
    const selected = this.activeOption;
    const index = options.indexOf(selected);
    const sibling = next ? options[index + 1] : options[index - 1];
    const defaultSibling = next ? options[0] : options[options.length - 1];

    this.activeOption = sibling || defaultSibling;
    if (this.activeOption) this.activeOption.scrollIntoView(false);
  },

  /**
   * Persists the active selection
   *
   * Scenario A: Links
   * If the result items are anchors, we simply click the link and
   * allow the link to be followed.
   *
   * Scenario B: Forms
   * If the result item is not a link, we assume that we are in the context
   * of a form and set a hidden input value that can be used
   * to send along with a form submission.
   *
   * * @param selected The active result item
   */
  commit(selected: Element) {
    this.resetResults();
    if (selected instanceof HTMLAnchorElement) {
      return;
    }

    const { listboxOptionValue, listboxOptionLabel } = (selected as any)
      .dataset;

    this.selectedValue = listboxOptionValue;
    this.selectedOption = selected;
    this.$refs.searchInput.value = listboxOptionLabel;
    this.$refs.searchInput.focus();

    this.$dispatch("combobox:change", {
      value: listboxOptionValue,
      label: listboxOptionLabel,
      selected: selected,
      inputName: this.config.comboboxInputName,
    });
  },

  /**
   * Fetches the default result list on page load, caching it so the list can be
   * restored without a refetch. Only used when fetching is not deferred.
   */
  fetchDefaultResults() {
    this.fetchResults("", true);
  },

  /**
   * Fetches the result list for the given query and renders it.
   *
   * @param query - The search query. Empty fetches the default list.
   * @param persistResults - If true, caches the response as the default list.
   */
  async fetchResults(query: string = "", persistResults = false) {
    const showSpinner = !persistResults;

    if (showSpinner) {
      this.results.isLoading = true;
    }

    try {
      this.$dispatch("combobox:loadstart", {
        inputName: this.config.comboboxInputName,
      });
      const queryParamObj = {};
      queryParamObj[this.config.comboboxQueryParam] = query;
      const response = await get(this.config.comboboxUrl, {
        query: queryParamObj,
      });

      if (response.ok) {
        const responseHTML = await response.html;

        if (persistResults) {
          this.results.defaultHTML = responseHTML;
        }

        this.replaceResults(responseHTML, !persistResults);

        this.$dispatch("combobox:load", {
          inputName: this.config.comboboxInputName,
        });
        this.$dispatch("combobox:loadend", {
          inputName: this.config.comboboxInputName,
        });
      } else {
        const responseError =
          (response && response.statusText) || response.status;
        throw Error(responseError);
      }
    } catch (error) {
      this.$dispatch("combobox:error", {
        message: error.message,
        inputName: this.config.comboboxInputName,
      });
      this.$dispatch("combobox:loadend", {
        inputName: this.config.comboboxInputName,
      });
    } finally {
      if (showSpinner) {
        this.results.isLoading = false;
      }
    }
  },

  /**
   * Renders the list of search results in the form of a menu component returned
   * from the server. If a menu is already present, it is replaced. The first
   * search result menu item is highlighted as active by default.
   *
   * @param html - The HTML containing the menu of search results.
   * @param showResults - If true, the results list is revealed.
   */
  replaceResults(html: string, showResults = true) {
    this.results.content = html;

    this.$nextTick(() => {
      this.results.visible = showResults;

      if (showResults) {
        this.selectSibling(true);
      }
    });
  },

  /**
   * Resets the search results list. When fetching is deferred, there is no
   * cached default list to fall back on, so the list is simply hidden.
   */
  resetResults() {
    if (this.deferFetch) {
      this.results.visible = false;
    } else {
      this.replaceResults(this.results.defaultHTML, false);
    }
  },

  get options(): Element[] {
    return Array.from(
      this.$refs.resultsContainer.querySelectorAll(optionSelector)
    );
  },
});
